/**
 * TransitionConditionCompiler - compiles and caches state transition conditions.
 */
import type {
  AnimationState,
  ConditionPredicate,
  TransitionCondition
} from './AnimationTypes.js';

export class TransitionConditionCompiler {
  private cache: WeakMap<object, ConditionPredicate> = new WeakMap();

  public compile(condition: TransitionCondition): ConditionPredicate {
    const evaluators: Array<(params: Map<string, number | boolean | string>, clipFinished: boolean) => boolean> = [];
    for (const [key, cond] of Object.entries(condition)) {
      if (key === 'clipFinished') {
        const expected = cond === true;
        evaluators.push((_, clipFinished) => clipFinished === expected);
        continue;
      }
      if (typeof cond === 'object' && cond !== null && !Array.isArray(cond)) {
        const rule = cond as {
          greaterThan?: number;
          greaterThanOrEqual?: number;
          lessThan?: number;
          lessThanOrEqual?: number;
          equalTo?: number | boolean | string;
          notEqualTo?: number | boolean | string;
        };
        evaluators.push((params) => {
          if (!params.has(key)) {
            throw new Error(`Parameter '${key}' does not exist in AnimationStateMachineComponent.`);
          }
          const param = params.get(key) as number | boolean | string;
          if ('greaterThan' in rule && !(typeof param === 'number' && param > (rule.greaterThan as number))) return false;
          if ('greaterThanOrEqual' in rule && !(typeof param === 'number' && param >= (rule.greaterThanOrEqual as number))) return false;
          if ('lessThan' in rule && !(typeof param === 'number' && param < (rule.lessThan as number))) return false;
          if ('lessThanOrEqual' in rule && !(typeof param === 'number' && param <= (rule.lessThanOrEqual as number))) return false;
          if ('equalTo' in rule && !(param === rule.equalTo)) return false;
          if ('notEqualTo' in rule && !(param !== rule.notEqualTo)) return false;
          return true;
        });
        continue;
      }
      evaluators.push((params) => {
        if (!params.has(key)) {
          throw new Error(`Parameter '${key}' does not exist in AnimationStateMachineComponent.`);
        }
        return params.get(key) === cond;
      });
    }
    return (params, clipFinished) => {
      for (const evaluate of evaluators) {
        if (!evaluate(params, clipFinished)) {
          return false;
        }
      }
      return true;
    };
  }

  public get(condition: TransitionCondition): ConditionPredicate {
    const cacheKey = condition as object;
    const existing = this.cache.get(cacheKey);
    if (existing) {
      return existing;
    }
    const compiled = this.compile(condition);
    this.cache.set(cacheKey, compiled);
    return compiled;
  }

  public matches(
    condition: TransitionCondition,
    params: Map<string, number | boolean | string>,
    clipFinished: boolean
  ): boolean {
    return this.get(condition)(params, clipFinished);
  }

  public precompileAll(states: Record<string, AnimationState>): void {
    for (const state of Object.values(states)) {
      for (const transition of state.transitions) {
        this.get(transition.condition);
      }
    }
  }
}
